import {ComponentRef, Directive, EventEmitter, HostListener, Input, OnInit, ViewContainerRef} from "@angular/core";
import {ScreenConfig, ScreenType, Utils} from "../utils/utils";
import { TooltipService } from "../services/tooltip.service";
import {
    TooltipAction,
    TooltipColor,
    TooltipConfig,
    TooltipData,
    TooltipEvent,
    TooltipEventAction,
    TooltipPosition,
    TooltipSize
} from "../components/tooltip/tooltip.component";

declare let window: any;

@Directive({
    selector: '[tooltip]'
})
export class TooltipDirective implements OnInit {
    @Input('tooltip-action') public tooltipAction: string;
    @Input('tooltip-event-emitter') eventEmitter: EventEmitter<TooltipEvent>;
    @Input('tooltip-id') tooltipId: string;
    @Input('tooltip-model') tooltipModel: any;
    @Input('tooltip-content') tooltipContent: string;
    @Input('tooltip-component') tooltipComponent: string;
    @Input('tooltip-class') tooltipClass: string;
    @Input('tooltip-enabled') tooltipEnabled: boolean;
    @Input('tooltip-config') tooltipConfig: TooltipConfig;
    @Input('tooltip-touch-enabled') touchEnabled: any;
    @Input('tooltip-position') tooltipPosition: any;
    @Input('tooltip-title') tooltipTitle: string;
    @Input('tooltip-color') tooltipColor: string;
    @Input('tooltip-size') tooltipSize: TooltipSize;
    @Input('tooltip-left-offset') tooltipLeftOffset: number;
    @Input('tooltip-top-offset') tooltipTopOffset: number;

    private _tooltipData: TooltipData;
    private _tooltipComponentRef: ComponentRef<any>;
    private _screenConfig: ScreenConfig;
    private _eventSubscription: any;
    private showTooltipTimer = null;

    constructor(
        private _tooltipsManager: TooltipService,
        private _viewContainerRef: ViewContainerRef,
        private _utils: Utils
    ) {
    }

    ngOnInit() {
        this._screenConfig = this._utils.screenConfig;

        if (this.tooltipConfig == null) {
            this.tooltipConfig = {enableTooltip: true};
        }

        if (this.tooltipEnabled != null) {
            this.tooltipConfig.enableTooltip = this.tooltipEnabled;
        }

        this.tooltipConfig.content = this.tooltipContent != null? this.tooltipContent : this.tooltipConfig.content;
        this.tooltipConfig.component = this.tooltipComponent != null? this.tooltipComponent : this.tooltipConfig.component;
        this.tooltipConfig.tooltipClass = this.tooltipClass != null? this.tooltipClass : this.tooltipConfig.tooltipClass;
        this.tooltipConfig.eventEmitter = this.eventEmitter != null? this.eventEmitter : this.tooltipConfig.eventEmitter;
        this.tooltipConfig.tooltipModel = this.tooltipModel != null? this.tooltipModel : this.tooltipConfig.tooltipModel;

        if (this.tooltipConfig.eventEmitter != null) {
            this._eventSubscription = this.tooltipConfig.eventEmitter.subscribe((event: TooltipEvent) => this.tooltipEventHandler(event));
        }

        if (this.tooltipConfig.eventEmitter != null) {
            if (this.tooltipAction == null) {
                this.tooltipConfig.tooltipAction = TooltipAction.EVENT;
            }

        } else if (this.tooltipAction == null) {
            this.tooltipConfig.tooltipAction = TooltipAction.HOVER;

        }

        if (this.tooltipConfig.tooltipAction == null) {
            this.tooltipConfig.tooltipAction = (<any>TooltipAction)[this.tooltipAction];
        }

        if (this.tooltipConfig.tooltipAction != TooltipAction.HOVER) {
            this.tooltipConfig.tooltipColor = TooltipColor.WHITE;

        } else {
            this.tooltipConfig.tooltipColor = this.tooltipColor != null? (<any>TooltipColor)[this.tooltipColor] : TooltipColor.BLACK;
        }

        let touchEnabled = this._getResposiveValue<boolean>(this.touchEnabled, "boolean", false);
        let tooltipPosition =  this._getResposiveValue<TooltipPosition>(this.tooltipPosition, "string", TooltipPosition.CENTER_TOP);

        this._tooltipData = {
            tooltipId               : this.tooltipId
            , tooltipConfig         : this.tooltipConfig
            , touchEnabled          : touchEnabled
            , tooltipPosition       : tooltipPosition
            , tooltipTitle          : this.tooltipTitle
            , tooltipShowClose      : this.tooltipConfig.tooltipAction != TooltipAction.HOVER
            , tooltipSize           : this.tooltipSize
            , topOffset             : this.tooltipTopOffset
            , leftOffset            : this.tooltipLeftOffset
        };
    }

    private _getResposiveValue<T>(value:any, type:string, defaultValue:T ) {
        let resultValue: T;

        if (typeof value == type) {
            resultValue = <T>(value);

        } else if (value != null && typeof value[this._screenConfig.type] != "undefined") {
            resultValue = <T>(value[this._screenConfig.type]);
        }

        return (resultValue) || defaultValue;
    }

    @HostListener('mouseenter')
    show() {
        if (this._screenConfig.type == ScreenType.DESKTOP && this._tooltipData.tooltipConfig.enableTooltip
            && this._tooltipData.tooltipConfig.tooltipAction == TooltipAction.HOVER) {

                this.showTooltipTimer = setTimeout(() => {                
                    this._tooltipData.tooltipPosition = this._getResposiveValue<TooltipPosition>(this.tooltipPosition, "string", TooltipPosition.CENTER_TOP);
                    this._tooltipComponentRef = this._tooltipsManager.showTooltip(this._tooltipData, this._viewContainerRef.element.nativeElement);
                }, 200);
                
        }
    }

    @HostListener('mouseleave')
    hide() { // el disableTooltipHide es para pruebas
        if (this._screenConfig.type == ScreenType.DESKTOP && this._tooltipData.tooltipConfig.enableTooltip
            && this._tooltipData.tooltipConfig.tooltipAction == TooltipAction.HOVER && typeof window.disableTooltipHide == "undefined") {
                clearTimeout(this.showTooltipTimer);
                this._hide();
        }
    }

    private _hide() {
        if (this._tooltipComponentRef != null) {
            this._tooltipComponentRef.instance.hide();
        }
    }

    private _touchClickEnabled() {
        this._tooltipData.touchEnabled = this._getResposiveValue<boolean>(this.touchEnabled, "boolean", false);
        return this._screenConfig.type != ScreenType.DESKTOP && this._tooltipData.tooltipConfig.tooltipAction == TooltipAction.HOVER && this._tooltipData.touchEnabled;
    }

    @HostListener('click', ['$event'])
    toggleVisibilityClick($event: any) {
        if (this._tooltipData.tooltipConfig.enableTooltip &&
            (this._tooltipData.tooltipConfig.tooltipAction == TooltipAction.CLICK || this._touchClickEnabled())
        ) {

            this._tooltipData.tooltipPosition =  this._getResposiveValue<TooltipPosition>(this.tooltipPosition, "string", TooltipPosition.CENTER_TOP);

            $event.stopPropagation();
            this._toggleVisibility();
        }
    }

    private _toggleVisibility() {
        if (this._tooltipData.tooltipConfig.enableTooltip) {
            if (this._tooltipComponentRef == null) {
                this._tooltipComponentRef = this._tooltipsManager.createTooltip(this._tooltipData, this._viewContainerRef.element.nativeElement);
            }

            this._tooltipComponentRef.instance.toggleVisibility();
        }
    }

    tooltipEventHandler(event: TooltipEvent) {
        // si existe el componente desde antes se va a atender al evento desde el mismo
        if (this._tooltipComponentRef == null && event.action != TooltipEventAction.HIDE) {
            this._tooltipComponentRef = this._tooltipsManager.createTooltip(this._tooltipData, this._viewContainerRef.element.nativeElement);
            this._tooltipComponentRef.instance.tooltipEventHandler(event);
        }
    }
}
